/**
 * EmailAttachmentChip — a self-contained, clickable chip that downloads one
 * email attachment through the gated backend endpoint.
 *
 * Owns its own download state machine (idle / fetching / error) so the
 * parent list only needs to pass attachment metadata — no shared loading
 * state, no lifted error handling.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react';
// Non-standard interactive chip shape (icon + filename + status text) that
// the styled lib/base Button cannot express — react-aria's Button keeps the
// keyboard/focus/press semantics. Same precedent as EmailThreadMessageCard's
// header row.
import { Button as AriaButton, Focusable } from 'react-aria-components';

import { formatFileSize, getFileIcon } from '@/lib/base/file-upload';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { fetchWithDefaults } from '@/lib/http/client';
import { triggerBlobDownload } from '@/lib/utils/fileDownload';
import { cx } from '@/utils/cx';

export interface EmailAttachmentChipProps {
  orgId: number;
  eventId: string;
  index: number;
  /** Optional/nullable — historical/Outlook payloads may omit it or send null (falls back below). */
  name?: string | null;
  /** Optional — omitted or 0 when the backend has no size (no size shown). */
  size?: number;
  /** Optional/nullable — the Outlook parser can emit a null contentType. */
  contentType?: string | null;
}

const FALLBACK_NAME = 'Attachment';

type ChipState =
  | { status: 'idle' }
  | { status: 'fetching' }
  | {
      status: 'error';
      kind: 'unavailable' | 'too_large' | 'disabled';
      retryable: boolean;
    };

function assertNever(x: never): never {
  throw new Error('unhandled: ' + JSON.stringify(x));
}

function resolveDownloadErrorKind(
  status: number
): 'unavailable' | 'too_large' | 'disabled' {
  if (status === 413) {
    return 'too_large';
  }
  if (status === 503) {
    return 'disabled';
  }
  // 502 | 404 | any other non-ok status.
  return 'unavailable';
}

const CHIP_BASE_CLASSES =
  'inline-flex max-w-full items-center gap-1.5 rounded-md bg-[var(--color-bg-secondary)] px-2 py-1 outline-hidden focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-inset';

const FILENAME_CLASSES =
  'min-w-0 max-w-[200px] truncate text-body-small text-[var(--color-text-primary)]';

const STATUS_TEXT_CLASSES =
  'shrink-0 text-body-small text-[var(--color-text-tertiary)]';

export const EmailAttachmentChip = memo(function EmailAttachmentChip({
  orgId,
  eventId,
  index,
  name,
  size,
  contentType,
}: EmailAttachmentChipProps) {
  const [state, setState] = useState<ChipState>({ status: 'idle' });
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    // Reset on mount: under StrictMode the effect runs mount → cleanup →
    // remount, and without this the ref would stay false after the first
    // cleanup, swallowing every guarded setState for the chip's lifetime.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const displayName = name || FALLBACK_NAME;
  const sizeLabel =
    typeof size === 'number' && size > 0 ? formatFileSize(size) : null;

  const handleDownload = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setState({ status: 'fetching' });
    try {
      const url = `/api/orgs/${orgId}/email/events/${eventId}/attachments/${index}/download`;
      const res = await fetchWithDefaults(url, { method: 'GET' }, 120000);
      if (res.ok) {
        const blob = await res.blob();
        // Use the same resolved name the chip displays so the saved file
        // matches the visible label instead of an unrelated "download".
        triggerBlobDownload(blob, displayName);
        if (mountedRef.current) {
          setState({ status: 'idle' });
        }
        return;
      }
      const kind = resolveDownloadErrorKind(res.status);
      if (mountedRef.current) {
        setState({ status: 'error', kind, retryable: kind === 'unavailable' });
      }
    } catch {
      // fetchWithDefaults throws TimeoutError/NetworkError rather than
      // resolving with res.ok === false.
      if (mountedRef.current) {
        setState({ status: 'error', kind: 'unavailable', retryable: true });
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [orgId, eventId, index, displayName]);

  const icon = (
    <span aria-hidden='true' className='flex shrink-0 items-center'>
      {getFileIcon(displayName, contentType ?? undefined, { size: 16 })}
    </span>
  );

  switch (state.status) {
    case 'idle':
      return (
        <AriaButton
          type='button'
          onPress={handleDownload}
          aria-label={`Download ${displayName}`}
          className={CHIP_BASE_CLASSES}
        >
          {icon}
          <span className={FILENAME_CLASSES}>{displayName}</span>
          {sizeLabel !== null && (
            <span className={STATUS_TEXT_CLASSES}>{sizeLabel}</span>
          )}
        </AriaButton>
      );
    case 'fetching':
      // `AriaButton` forwards props through react-aria's `filterDOMProps`,
      // which drops `aria-busy`, so the busy state lives on a native `<span>`
      // wrapper instead — a real DOM element keeps the attribute and exposes
      // the state to assistive technology.
      return (
        <span aria-busy='true' className='inline-flex max-w-full'>
          <AriaButton
            type='button'
            isDisabled
            aria-label={`Downloading ${displayName}`}
            className={CHIP_BASE_CLASSES}
          >
            {icon}
            <span className={FILENAME_CLASSES}>{displayName}</span>
            <LoadingIndicator inline size='sm' label='Downloading…' />
          </AriaButton>
        </span>
      );
    case 'error':
      if (state.retryable) {
        return (
          <Tooltip title="We couldn't download this file. Try again.">
            <AriaButton
              type='button'
              onPress={handleDownload}
              aria-label={`Download ${displayName} – not available, try again`}
              className={CHIP_BASE_CLASSES}
            >
              {icon}
              <span className={FILENAME_CLASSES}>{displayName}</span>
              <span className={STATUS_TEXT_CLASSES}>
                Not available – try again
              </span>
            </AriaButton>
          </Tooltip>
        );
      }
      return (
        <span className={CHIP_BASE_CLASSES}>
          {icon}
          <span className={FILENAME_CLASSES}>{displayName}</span>
          <Tooltip
            title={
              state.kind === 'too_large'
                ? 'This file is too large to download.'
                : 'Downloads are off.'
            }
          >
            <Focusable>
              <span
                aria-label={
                  state.kind === 'too_large'
                    ? `${displayName}, too large to download`
                    : `${displayName}, downloads are off`
                }
                className={cx(
                  STATUS_TEXT_CLASSES,
                  'cursor-default',
                  'rounded-md outline-hidden focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-inset'
                )}
              >
                {state.kind === 'too_large'
                  ? 'Too large to download'
                  : 'Downloads are off'}
              </span>
            </Focusable>
          </Tooltip>
        </span>
      );
    default:
      return assertNever(state);
  }
});
